import React, { useContext, useEffect } from 'react'
import { AppSettings } from '../../config/app-settings'
import {
  Card,
  CardBody,
  CardFooter,
  CardHeader
} from '../../components/other/card/card'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import TextArea from 'antd/es/input/TextArea'
import { useChangeRemovalStatusMutation } from '../../redux/apis/linkManagement/ugc/lMUgcSourceApiSlice'
import Loader from '../pages/loader'
import { toast } from 'react-toastify'
import { getUrlLines, isValidHttpUrl } from '../../helpers/url'

const ChangeRemovalStatus = React.memo(() => {
  const context = useContext(AppSettings)
  const [changeRemovalStatus, { isLoading, error }] =
    useChangeRemovalStatusMutation()

  const validation = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
    initialValues: {
      urls: ''
    },
    validationSchema: Yup.object({
      urls: Yup.string()
        .test(
          'at-least-one-url',
          'Please enter at least one URL',
          value => getUrlLines(value || '').length > 0
        )
        .test('each-line-valid-url', function (value) {
          const lines = value || ''
          const invalid = []
          lines.split(/\r?\n/).forEach((line, idx) => {
            const t = line.trim()
            if (t === '') return
            if (!isValidHttpUrl(t)) {
              const preview = t.length > 56 ? `${t.slice(0, 56)}…` : t
              invalid.push({ line: idx + 1, preview })
            }
          })
          if (invalid.length === 0) return true
          const head =
            "Some lines don't look like valid links yet. Please enter one complete URL per line. Please check:"
          const rest = invalid
            .map(({ line, preview }) => `Line ${line}: ${preview}`)
            .join('\n')
          return this.createError({ message: `${head}\n${rest}` })
        })
    }),

    onSubmit: async values => {
      const urls = getUrlLines(values.urls)

      try {
        const res = await changeRemovalStatus({ urls }).unwrap()
        if (res?.success) {
          toast.success(res?.message || 'Removal status updated successfully.')
          const updated = res?.data?.updated
          toast.info(`Updated: ${updated != null ? updated : '?'} records`)
          validation.resetForm()
        } else {
        }
      } catch (err) {
        toast.error(err?.message || 'Failed to update removal status.')
      }
    }
  })

  useEffect(() => {
    if (error?.data) {
      error.data.message
        ? toast.error(error.data.message)
        : Object.values(error.data)
            .flat()
            .forEach(message => {
              toast.error(message)
            })
    }
  }, [error])

  useEffect(() => {
    context.setAppHeaderNone(false)
    context.setAppSidebarNone(false)
    context.setAppContentClass('')
    context.setAppThemePanal(true)

    return () => {
      context.setAppHeaderNone(true)
      context.setAppSidebarNone(true)
      context.setAppContentClass('p-0')
      context.setAppThemePanal(false)
    }
  }, [context])

  return (
    <div>
      <Loader isLoading={isLoading} />
      <Card>
        <CardHeader>
          <p className='mb-0'>Update Links</p>
          <p className='text-muted small mb-0'>Change Removal Status</p>
        </CardHeader>
        <CardBody>
          <div
            className='alert mb-4 rounded'
            role='status'
            style={{
              color: 'var(--bs-theme)',
              border: '1px solid var(--bs-theme)',
              backgroundColor: 'rgba(var(--bs-theme-rgb), 0.14)'
            }}
          >
            <p className='mb-2 fw-semibold'>Before you paste URLs</p>
            <ul className='mb-0 ps-3 small'>
              <li className='mb-2'>
                <strong>What this does:</strong> clears a <strong>recent removal</strong> on
                matching <strong>UGC source</strong> links—so they count as active again.
              </li>
              <li className='mb-2'>
                <strong>When this applies:</strong> only for links removed in{' '}
                <strong>this month or last month</strong>. Older removals, and URLs we don&apos;t
                already have, are not updated.
              </li>
              <li className='mb-0'>
                <strong>How to enter them:</strong> one full URL per line (press Enter between
                links). Paste from a spreadsheet or report if you like.
              </li>
            </ul>
          </div>
          <div className='mb-3 flex-1 customInput'>
            <label className='form-label' htmlFor='change-removal-status-urls'>
              Paste URLs (one per line)
            </label>
            <TextArea
              id='change-removal-status-urls'
              type='text'
              name='urls'
              onChange={e => {
                validation.handleChange(e)
                if (validation.errors.urls) {
                  validation.setFieldError('urls', undefined)
                }
              }}
              onBlur={validation.handleBlur}
              value={validation.values.urls}
              placeholder={`Type or paste links here — one URL per line (press Enter between each).

Use full links starting with https:// or http:// only. If you pasted from Excel or a report, delete extra commas, quotes, or labels so each line is just the URL.

Examples:
https://www.youtube.com/watch?v=xxxxxxxxxxx
https://example.com/watch/show-name/episode-1
https://partner.site.com/stream?id=12345`}
              rows={15}
            />
            {validation.submitCount > 0 && validation.errors.urls ? (
              <div className='text-danger small mt-1' role='alert'>
                {String(validation.errors.urls).includes('\n') ? (
                  <>
                    <p className='mb-1 fw-semibold'>
                      {String(validation.errors.urls).split('\n')[0]}
                    </p>
                    <ul className='mb-0 ps-3'>
                      {String(validation.errors.urls)
                        .split('\n')
                        .slice(1)
                        .filter(Boolean)
                        .map((line, i) => (
                          <li key={i}>{line}</li>
                        ))}
                    </ul>
                  </>
                ) : (
                  <p className='mb-0'>{validation.errors.urls}</p>
                )}
              </div>
            ) : null}
          </div>
        </CardBody>
        <CardFooter>
          <div className='flex justify-end gap-8'>
            <button
              onClick={() => {
                validation.handleSubmit()
              }}
              className='btn btn-theme'
            >
              Submit
            </button>
          </div>
        </CardFooter>
      </Card>
    </div>
  )
})

export default ChangeRemovalStatus
